import { useEffect, useRef, useState } from "react";
import {
  Platform,
  StyleSheet,
  View,
  type AccessibilityActionEvent,
  type GestureResponderHandlers,
  type ViewStyle,
} from "react-native";
import { useTranslation } from "react-i18next";

import { Icon } from "../Icon/Icon";
import type { UiTheme } from "../theme";

const WEB = Platform.OS === "web";

// RN has no `touchAction` style: on native the responder already keeps the
// sheet's scroll off the drag. On web the browser would pan the page under a
// touch drag, so the handle opts out. Typed once, here.
const webNoPan = (WEB ? { touchAction: "none" } : {}) as unknown as ViewStyle;

// HR-11: the grip on an active row of the Home config sheet. Three ways to
// move a row, one callback:
//   · drag (the responder handlers from useSortableDrag);
//   · native screen readers: the "move up" / "move down" actions;
//   · web keyboard: focus the grip, then ArrowUp / ArrowDown. RNW drops
//     accessibilityActions, so the keys are the web door.
export function HomeDragHandle({
  theme,
  label,
  position,
  canMoveUp,
  canMoveDown,
  onMoveBy,
  handlers,
}: {
  theme: UiTheme;
  label: string;
  /** The row's slot. A change after a keyboard move puts focus back here. */
  position: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveBy: (delta: -1 | 1) => void;
  handlers: GestureResponderHandlers;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const ref = useRef<View>(null);
  const [focused, setFocused] = useState(false);
  const refocus = useRef(false);

  // Reordering moves the row's DOM node, and a moved node loses focus.
  useEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    ref.current?.focus();
  }, [position]);

  const onKeyDown = (e: { key: string; preventDefault: () => void }) => {
    const delta =
      e.key === "ArrowUp" && canMoveUp
        ? -1
        : e.key === "ArrowDown" && canMoveDown
          ? 1
          : 0;
    if (delta === 0) return;
    e.preventDefault();
    refocus.current = true;
    onMoveBy(delta);
  };
  const webProps = WEB
    ? {
        focusable: true,
        onKeyDown,
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
      }
    : {};

  const actions = [
    ...(canMoveUp
      ? [
          {
            name: "moveUp",
            label: t("mobile.home.config.moveUp", { defaultValue: "Move up" }),
          },
        ]
      : []),
    ...(canMoveDown
      ? [
          {
            name: "moveDown",
            label: t("mobile.home.config.moveDown", {
              defaultValue: "Move down",
            }),
          },
        ]
      : []),
  ];
  const onAction = (e: AccessibilityActionEvent) => {
    if (e.nativeEvent.actionName === "moveUp") onMoveBy(-1);
    if (e.nativeEvent.actionName === "moveDown") onMoveBy(1);
  };

  return (
    <View
      ref={ref}
      {...handlers}
      {...webProps}
      accessible
      accessibilityLabel={
        WEB
          ? t("mobile.home.config.reorderKeys", {
              name: label,
              defaultValue: "Reorder {{name}}, use arrow keys",
            })
          : t("mobile.home.config.reorder", {
              name: label,
              defaultValue: "Reorder {{name}}",
            })
      }
      accessibilityHint={t("mobile.home.config.reorderHint", {
        defaultValue: "Drag to move, or use the actions to move up or down.",
      })}
      accessibilityActions={actions}
      onAccessibilityAction={onAction}
      style={[styles.handle, webNoPan, focused && styles.focused]}
    >
      <Icon
        name="grip-vertical"
        size={theme.typography.sizes.md}
        color={theme.palette.ink2}
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, radius: r } = theme;
  return StyleSheet.create({
    handle: {
      width: 44,
      height: 44,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: r.r2,
      borderWidth: 2,
      borderColor: "transparent",
    },
    // Keyboard focus: a ring in the ink colour, so the grip that the arrow
    // keys will move is always the one outlined.
    focused: { borderColor: p.ink },
  });
}
